import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ExternalE2EEKeyProvider, Room, RoomEvent, Track } from "livekit-client";
import { ArrowLeft, Camera, CameraOff, Mic, MicOff, Settings, ShieldCheck, Upload } from "lucide-react";
import NationStudioPanelV3 from "../components/NationStudioPanelV3";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HAND_TOPIC = "nation-hand-v1";
const VAULT_DB = "nation-communications-vault";
const VAULT_STORE = "room-keys";
const DEVICE_KEY_ID = "device-wrap-key";

const CHAT_TOPIC = "nation-chat-v1";
const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

function b64url(bytes) { let binary=""; for(const b of new Uint8Array(bytes)) binary+=String.fromCharCode(b); return btoa(binary).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/g,""); }
function fromB64url(value) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}
async function decryptRoomPacket(rawKey, roomId, topic, packet) {
  const envelope = JSON.parse(new TextDecoder().decode(packet));
  const key = await crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["decrypt"]);
  const plaintext = await crypto.subtle.decrypt({
    name: "AES-GCM",
    iv: fromB64url(envelope.iv),
    additionalData: new TextEncoder().encode(`${roomId}|${topic}`),
  }, key, fromB64url(envelope.ciphertext));
  return JSON.parse(new TextDecoder().decode(plaintext));
}
function readFragmentKey() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const key = params.get("key");
  if (!key) return null;
  const bytes = fromB64url(key);
  return bytes.length === 32 ? bytes : null;
}
async function packetKey(rawKey) {
  return crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["encrypt","decrypt"]);
}
async function encryptPacket(rawKey,roomId,topic,value){const key=await packetKey(rawKey);const iv=crypto.getRandomValues(new Uint8Array(12));const ciphertext=await crypto.subtle.encrypt({name:"AES-GCM",iv,additionalData:new TextEncoder().encode(`${roomId}|${topic}`)},key,new TextEncoder().encode(JSON.stringify(value)));return new TextEncoder().encode(JSON.stringify({iv:b64url(iv),ciphertext:b64url(ciphertext)}));}
async function decryptPacket(rawKey, roomId, topic, packet) {
  const envelope = JSON.parse(new TextDecoder().decode(packet));
  const key = await packetKey(rawKey);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromB64url(envelope.iv), additionalData: new TextEncoder().encode(`${roomId}|${topic}`) },
    key,
    fromB64url(envelope.ciphertext)
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}
function openVault() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VAULT_DB, 1);
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(VAULT_STORE)) req.result.createObjectStore(VAULT_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function vaultGet(key) {
  const db = await openVault();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(VAULT_STORE, "readonly");
    const req = tx.objectStore(VAULT_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}
async function vaultPut(key,value){const db=await openVault();return new Promise((resolve,reject)=>{const tx=db.transaction(VAULT_STORE,"readwrite");tx.objectStore(VAULT_STORE).put(value,key);tx.oncomplete=()=>{db.close();resolve();};tx.onerror=()=>{db.close();reject(tx.error);};});}
async function getDeviceWrapKey(){let key=await vaultGet(DEVICE_KEY_ID);if(key)return key;key=await crypto.subtle.generateKey({name:"AES-GCM",length:256},false,["encrypt","decrypt"]);await vaultPut(DEVICE_KEY_ID,key);return key;}
async function rememberRoomKey(roomId,roomKey){const wrapKey=await getDeviceWrapKey();const iv=crypto.getRandomValues(new Uint8Array(12));const ciphertext=await crypto.subtle.encrypt({name:"AES-GCM",iv,additionalData:new TextEncoder().encode(`nation-room-vault|${roomId}`)},wrapKey,roomKey);await vaultPut(`room:${roomId}`,{iv:b64url(iv),ciphertext:b64url(ciphertext)});}
async function loadRememberedRoomKey(roomId) {
  const record = await vaultGet(`room:${roomId}`);
  if (!record) return null;
  const wrapKey = await vaultGet(DEVICE_KEY_ID);
  if (!wrapKey) return null;
  try {
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64url(record.iv), additionalData: new TextEncoder().encode(`nation-room-vault|${roomId}`) }, wrapKey, fromB64url(record.ciphertext));
    return new Uint8Array(plaintext);
  } catch { return null; }
}
function initials(name) {
  return String(name || "Studio User").trim().split(/\s+/).slice(0, 2).map((v) => v[0]?.toUpperCase()).join("") || "SU";
}
function PersonalAvatar({ name, url, className = "" }) {
  if (url) return <img src={url} alt={`Avatar for ${name || "participant"}`} className={`rounded-full object-cover ${className}`} />;
  return <div className={`rounded-full bg-[#173d78] text-white flex items-center justify-center font-bold ${className}`}>{initials(name)}</div>;
}

function DeviceLobby({ roomId, roomMeta, onEnter, onBack }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserFrameRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [cameraId, setCameraId] = useState("");
  const [micId, setMicId] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);
  const [micLevel, setMicLevel] = useState(0);
  const [displayName, setDisplayName] = useState(roomMeta?.created_by_display_name || "Studio host");
  const [headline, setHeadline] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);

  const stopMeter = () => {
    if (analyserFrameRef.current) cancelAnimationFrame(analyserFrameRef.current);
    analyserFrameRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    setMicLevel(0);
  };
  const startMeter = (stream) => {
    stopMeter();
    const track = stream?.getAudioTracks?.()[0];
    if (!track) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    audioContextRef.current = ctx;
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(samples);
      let total = 0;
      samples.forEach((value) => { const centered = (value - 128) / 128; total += centered * centered; });
      const rms = Math.sqrt(total / samples.length);
      setMicLevel(Math.min(100, Math.round(rms * 360)));
      analyserFrameRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const stopPreview = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    stopMeter();
  };
  const startPreview = async (nextCamera = cameraId, nextMic = micId, desiredCamera = cameraOn, desiredMic = micOn) => {
    stopPreview(); setChecking(true); setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: desiredCamera ? { deviceId: nextCamera ? { exact: nextCamera } : undefined } : false,
        audio: desiredMic ? { deviceId: nextMic ? { exact: nextMic } : undefined } : false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.muted = true; }
      if (desiredMic) startMeter(stream);
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({ cameras: all.filter((d) => d.kind === "videoinput"), microphones: all.filter((d) => d.kind === "audioinput") });
    } catch (err) {
      setError(err?.name === "NotAllowedError" ? "Camera or microphone permission is blocked in this browser. Allow access from the address bar, then try again." : err?.message || "Could not open camera/microphone.");
    } finally { setChecking(false); }
  };

  useEffect(() => {
    axios.get(`${API}/communications/avatar/profile`).then((response) => {
      if (response.data?.url) setAvatarUrl(response.data.url);
    }).catch(() => {});
    startPreview();
    return stopPreview;
    // Lobby intentionally initializes one device preview on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = async () => {
    const next = !micOn; setMicOn(next); await startPreview(cameraId, micId, cameraOn, next);
  };
  const toggleCamera = async () => {
    const next = !cameraOn; setCameraOn(next); await startPreview(cameraId, micId, next, micOn);
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { setError("Avatar must be JPG, PNG, or WebP."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Avatar must be 5 MB or smaller."); return; }
    setAvatarUploading(true); setError("");
    try {
      const form = new FormData();
      form.append("room_id", roomId);
      form.append("file", file);
      const response = await axios.post(`${API}/communications/avatar/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarUrl(response.data?.url || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Avatar could not be uploaded.");
    } finally { setAvatarUploading(false); }
  };

  return <div className="min-h-screen bg-[#070a10] text-white flex items-center justify-center p-6">
    <div className="w-full max-w-5xl">
      <div className="mb-4"><Button type="button" variant="outline" onClick={onBack} className="border-slate-600 text-white"><ArrowLeft className="w-4 h-4 mr-2"/>Back</Button></div>
      <div className="text-center mb-6"><img src={COAT_OF_ARMS} alt="Nation coat of arms" className="w-12 h-12 rounded-full object-cover mx-auto mb-3"/><h1 className="font-heading text-3xl">Set up your Studio Meeting</h1><div className="text-sm text-slate-400 mt-1">{roomMeta?.title || "Yasharal Live Studio"} · entering Studio does not start the broadcast.</div></div>
      <div className="grid md:grid-cols-[1.45fr_1fr] gap-5">
        <div className="rounded-2xl border border-slate-700 bg-[#111318] overflow-hidden">
          <div className="aspect-video bg-black flex items-center justify-center relative">
            {cameraOn ? <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover"/> : <div className="text-center"><PersonalAvatar name={displayName} url={avatarUrl} className="w-24 h-24 mx-auto text-2xl"/><div className="mt-3 text-slate-400">Camera off</div></div>}
            <div className="absolute bottom-3 left-3 rounded bg-black/70 px-3 py-1 text-sm">{displayName}</div>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2"><Button variant="outline" className="border-slate-600 text-white" onClick={toggleMic}>{micOn?<Mic className="w-4 h-4 mr-2"/>:<MicOff className="w-4 h-4 mr-2"/>}{micOn?"Mute":"Unmute"}</Button><Button variant="outline" className="border-slate-600 text-white" onClick={toggleCamera}>{cameraOn?<Camera className="w-4 h-4 mr-2"/>:<CameraOff className="w-4 h-4 mr-2"/>}{cameraOn?"Stop camera":"Start camera"}</Button><Button variant="outline" className="border-slate-600 text-white" onClick={()=>startPreview()}><Settings className="w-4 h-4 mr-2"/>Check devices</Button></div>
            <div className="flex items-center gap-3"><Mic className="w-4 h-4 text-slate-400"/><div className="h-2 flex-1 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-400 transition-all" style={{width:`${micOn?micLevel:0}%`}}/></div><span className="text-xs text-slate-400 w-16">Mic check</span></div>
            {error&&<div className="text-sm text-red-300">{error}</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-[#111318] p-5 space-y-4">
          <div><label className="text-sm font-semibold">Display name</label><input value={displayName} onChange={(e)=>setDisplayName(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"/></div>
          <div><label className="text-sm font-semibold">Headline <span className="text-slate-500 font-normal">(optional)</span></label><input value={headline} onChange={(e)=>setHeadline(e.target.value)} placeholder="Role, function, topic, etc." className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"/></div>
          <div><label className="text-sm font-semibold">Avatar when camera is off</label><div className="mt-2 rounded-xl border border-slate-700 p-3 flex items-center gap-3"><PersonalAvatar name={displayName} url={avatarUrl} className="w-14 h-14 text-lg"/><div className="flex-1"><div className="text-sm">{avatarUrl?"Your saved avatar":"Initials are the default"}</div><div className="text-xs text-slate-500 mt-1">Your uploaded avatar is reused in future meetings and Studio sessions.</div></div><Button size="sm" variant="outline" disabled={avatarUploading} onClick={()=>avatarInputRef.current?.click()} className="border-slate-600 text-white"><Upload className="w-4 h-4 mr-1"/>{avatarUploading?"Uploading…":"Upload"}</Button><input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e)=>{const file=e.target.files?.[0];if(file)uploadAvatar(file);e.target.value="";}}/></div></div>
          <div><label className="text-sm font-semibold">Microphone</label><select value={micId} onChange={async(e)=>{setMicId(e.target.value);await startPreview(cameraId,e.target.value,cameraOn,micOn);}} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"><option value="">Default microphone</option>{devices.microphones.map((d)=><option key={d.deviceId} value={d.deviceId}>{d.label || "Microphone"}</option>)}</select></div>
          <div><label className="text-sm font-semibold">Camera</label><select value={cameraId} onChange={async(e)=>{setCameraId(e.target.value);await startPreview(e.target.value,micId,cameraOn,micOn);}} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"><option value="">Default camera</option>{devices.cameras.map((d)=><option key={d.deviceId} value={d.deviceId}>{d.label || "Camera"}</option>)}</select></div>
          <Button disabled={checking||!displayName.trim()} onClick={()=>{stopPreview();onEnter({cameraOn,micOn,cameraId,micId,displayName:displayName.trim(),headline:headline.trim(),avatarUrl});}} className="w-full bg-[#8bb6ff] text-slate-950">Enter Studio Meeting</Button>
          
        </div>
      </div>
    </div>
  </div>;
}

export default function NationStudio() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const roomRef = useRef(null);
  const [roomMeta, setRoomMeta] = useState(null);
  const [roomKey, setRoomKey] = useState(null);
  const [entered, setEntered] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const [spotlightId, setSpotlightId] = useState(null);
  const [activeSpeakerIds, setActiveSpeakerIds] = useState([]);
  const [localAvatarUrl, setLocalAvatarUrl] = useState("");
  const [avatarMap, setAvatarMap] = useState({});
  const [raisedHands, setRaisedHands] = useState({});
  const [meetingMessages, setMeetingMessages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([axios.get(`${API}/communications/rooms/${roomId}`), loadRememberedRoomKey(roomId)]).then(async ([metaRes, key]) => {
      if (cancelled) return;
      if (!metaRes.data?.can_open_studio) { setError("Only the session host, co-host, or authorized Communications manager can open Yasharal Live Studio."); return; }
      setRoomMeta(metaRes.data);
      let resolved = readFragmentKey() || key;
      if (!resolved) {
        resolved = crypto.getRandomValues(new Uint8Array(32));
        await rememberRoomKey(roomId, resolved);
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        fragment.set("key", b64url(resolved));
        window.history.replaceState(null, "", `${window.location.pathname}#${fragment.toString()}`);
      }
      setError("");
      setRoomKey(resolved);
    }).catch((err)=>{if(err?.response?.status===401)navigate("/nationals/login");else setError(err?.response?.data?.detail||err?.message||"Could not open Yasharal Live Studio.");});
    return ()=>{cancelled=true;};
  }, [roomId,navigate]);

  const announceSession = (active) => window.dispatchEvent(new CustomEvent("communications-session-state", { detail: { active, roomId, mode: "studio" } }));
  const refreshRoomMeta = async () => {
    try {
      const response = await axios.get(`${API}/communications/rooms/${roomId}`);
      setRoomMeta(response.data || null);
      return response.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!roomId) return undefined;
    const timer = window.setInterval(refreshRoomMeta, 3000);
    return () => window.clearInterval(timer);
  }, [roomId]);

  const enterStudio = async ({ cameraOn, micOn, cameraId, micId, displayName, headline, avatarUrl }) => {
    try { window.localStorage.setItem(`yasharal_studio_resume_${roomId}`, JSON.stringify({displayName:displayName||"",headline:headline||"",avatarUrl:avatarUrl||"",cameraId:cameraId||"",micId:micId||""})); } catch {}
    setEntered(true); setError(""); setLocalAvatarUrl(avatarUrl || "");
    try {
      const tokenRes = await axios.post(`${API}/communications/room-token`, { room_id: roomId, display_name: displayName || undefined, headline: headline || undefined });
      const provider = new ExternalE2EEKeyProvider({ keySize: 256 });
      await provider.setKey(roomKey.buffer.slice(roomKey.byteOffset, roomKey.byteOffset + roomKey.byteLength));
      const worker = new Worker(new URL("livekit-client/e2ee-worker", import.meta.url));
      const room = new Room({ adaptiveStream:false, dynacast:false, encryption:{ keyProvider:provider, worker } });
      const refresh=()=>setTick((v)=>v+1);
      room.on(RoomEvent.ParticipantConnected,refresh); room.on(RoomEvent.ParticipantDisconnected,(participant)=>{setRaisedHands((current)=>{const next={...current};delete next[participant.identity];return next;});refresh();}); room.on(RoomEvent.TrackSubscribed,refresh); room.on(RoomEvent.TrackUnsubscribed,refresh); room.on(RoomEvent.LocalTrackPublished,refresh); room.on(RoomEvent.LocalTrackUnpublished,refresh); room.on(RoomEvent.TrackMuted,refresh); room.on(RoomEvent.TrackUnmuted,refresh); room.on(RoomEvent.ActiveSpeakersChanged,(speakers)=>{const ids=(speakers||[]).map((s)=>s.identity);setActiveSpeakerIds(ids);if(ids[0])setSpotlightId((current)=>current||ids[0]);}); room.on(RoomEvent.DataReceived,async(packet,participant,_kind,topic)=>{if(!participant)return;try{if(topic===HAND_TOPIC){const decoded=await decryptPacket(roomKey,roomId,HAND_TOPIC,packet);setRaisedHands((current)=>({...current,[participant.identity]:Boolean(decoded.raised)}));}else if(topic===CHAT_TOPIC){const decoded=await decryptPacket(roomKey,roomId,CHAT_TOPIC,packet);setMeetingMessages(items=>[...items,{...decoded,from:participant.name||participant.identity||"Participant"}]);}}catch{}}); room.on(RoomEvent.Disconnected,()=>{setConnected(false);announceSession(false);});
      room.on(RoomEvent.DataReceived, async (packet, participant, _kind, topic) => {
        if (topic !== HAND_TOPIC || !roomKey) return;
        try {
          const decoded = await decryptRoomPacket(roomKey, roomId, HAND_TOPIC, packet);
          setRaisedHands((current) => ({ ...current, [participant?.identity]: Boolean(decoded.raised) }));
        } catch {}
      });
      await room.connect(tokenRes.data.url,tokenRes.data.token); await room.setE2EEEnabled(true); roomRef.current=room;
      // Release the lobby preview before entering Studio. Keeping two camera
      // acquisitions open can cause device contention and a second LED flash.
      // Lobby owns stopPreview; release its media before invoking enterStudio.
      if (micOn) await room.localParticipant.setMicrophoneEnabled(true, micId ? { deviceId: micId } : undefined);
      if (cameraOn) {
        // Publish a user-provided browser capture track. LiveKit-managed camera
        // tracks restart getUserMedia on unmute; user-provided tracks do not.
        const cameraStream = await navigator.mediaDevices.getUserMedia({
          video: cameraId ? { deviceId: { exact: cameraId } } : true,
          audio: false,
        });
        const videoTrack = cameraStream.getVideoTracks()[0];
        try {
          await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera });
        } catch (error) {
          videoTrack?.stop();
          throw error;
        }
      }
      setConnected(true); announceSession(true); refresh();
    } catch(err){setEntered(false);announceSession(false);setError(err?.response?.data?.detail||err?.message||"Could not enter Yasharal Live Studio.");}
  };

  useEffect(()=>{
    if(!roomMeta||!roomKey||entered||connected)return;
    const active=Boolean(roomMeta.studio_egress_id||roomMeta.broadcast_active||roomMeta.recording_active);
    if(!active)return;
    let saved={};
    try{saved=JSON.parse(window.localStorage.getItem(`yasharal_studio_resume_${roomId}`)||"{}");}catch{}
    enterStudio({
      cameraOn:false,
      micOn:false,
      cameraId:saved.cameraId||"",
      micId:saved.micId||"",
      displayName:saved.displayName||roomMeta.created_by_display_name||"Studio host",
      headline:saved.headline||"",
      avatarUrl:saved.avatarUrl||"",
    });
  },[roomMeta,roomKey,entered,connected,roomId]);

  useEffect(()=>()=>{announceSession(false);roomRef.current?.disconnect();roomRef.current=null;},[]);

  const sendMeetingChat=async(text)=>{const message=String(text||"").trim();if(!message||!roomRef.current)return;const item={text:message,at:new Date().toISOString()};const payload=await encryptPacket(roomKey,roomId,CHAT_TOPIC,item);await roomRef.current.localParticipant.publishData(payload,{reliable:true,topic:CHAT_TOPIC});setMeetingMessages(items=>[...items,{...item,from:"You"}]);};

  const rawParticipants = useMemo(()=>{
    const room=roomRef.current;if(!room)return[];
    const build=(p,isLocal)=>{let camera=null,screen=null,microphone=null;p.trackPublications.forEach((pub)=>{if(pub.source===Track.Source.Camera)camera=pub;if(pub.source===Track.Source.ScreenShare)screen=pub;if(pub.source===Track.Source.Microphone)microphone=pub;});return{identity:p.identity,name:p.name||p.identity,camera,screen,microphone,isLocal,liveParticipant:p};};
    const list=[build(room.localParticipant,true)]; room.remoteParticipants.forEach((p)=>list.push(build(p,false))); return list;
  },[tick,connected]);

  useEffect(()=>{
    if(!connected||!rawParticipants.length)return;
    const ids=rawParticipants.map((p)=>p.identity).filter(Boolean);
    axios.post(`${API}/communications/rooms/${roomId}/participant-profiles`,{identities:ids}).then((response)=>setAvatarMap(response.data?.profiles||{})).catch(()=>{});
  },[connected,roomId,rawParticipants]);

  const participants = useMemo(()=>rawParticipants.map((p)=>({
    ...p,
    avatarUrl: p.isLocal ? localAvatarUrl : avatarMap[p.identity]?.avatar_url || "",
  })),[rawParticipants,localAvatarUrl,avatarMap]);

  useEffect(()=>{if(!spotlightId&&participants.length)setSpotlightId(participants[0].identity);},[participants,spotlightId]);

  if(error&&!entered)return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6"><Card className="max-w-xl bg-slate-900 border-slate-700 text-white"><CardContent className="p-8 text-center space-y-4"><img src={COAT_OF_ARMS} alt="Nation coat of arms" className="w-20 h-20 rounded-full object-cover mx-auto"/><ShieldCheck className="w-8 h-8 text-kingdom-gold mx-auto"/><h1 className="font-heading text-2xl">Yasharal Live Studio</h1><p className="text-slate-300">{error}</p><Button onClick={()=>navigate("/yasharal-live")} className="bg-royal-blue text-white"><ArrowLeft className="w-4 h-4 mr-2"/>Yasharal Live Home</Button></CardContent></Card></div>;
  if(!roomMeta||!roomKey)return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"><div className="text-center"><img src={COAT_OF_ARMS} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-4"/><p>Opening secure Yasharal Live Studio…</p></div></div>;
  if(!entered)return <DeviceLobby roomId={roomId} roomMeta={roomMeta} onEnter={enterStudio} onBack={()=>navigate("/yasharal-live")}/>;
  if(!connected)return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center"><div className="text-center"><ShieldCheck className="w-10 h-10 text-kingdom-gold mx-auto mb-4"/><p>Connecting secure Studio…</p></div></div>;

  return <><NationStudioPanelV3 roomId={roomId} roomMeta={roomMeta} meetingRoom={roomRef.current} participants={participants} spotlightId={spotlightId} onSpotlight={setSpotlightId} activeSpeakerIds={activeSpeakerIds} raisedHands={raisedHands} secureRoomKey={roomKey} meetingMessages={meetingMessages} onSendMeetingChat={sendMeetingChat}/><div className="fixed top-3 left-52 z-[90] flex gap-2"><Button size="sm" variant="outline" onClick={()=>navigate("/yasharal-live")} className="border-[#8bb6ff]/60 bg-[#111318]/95 text-white"><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#8bb6ff]"/>Yasharal Live Home</Button></div></>;
}
